import React, { useEffect, useRef, useState } from "react";
import "./Contact.css";
import { sendEmailJS } from "../utils/smtp";

const RECAPTCHA_SITE_KEY = process.env.REACT_RECAPTCHA_SITE_KEY || "your-site-key-here";

export default function ContactSection() {
  const recaptchaRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [recaptchaReady, setRecaptchaReady] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState("");
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sendStatus, setSendStatus] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (window.grecaptcha && typeof window.grecaptcha.render === "function") {
      setRecaptchaReady(true);
      return undefined;
    }
    if (document.getElementById("recaptcha-script")) return undefined;
    const script = document.createElement("script");
    script.id = "recaptcha-script";
    script.src = "https://www.google.com/recaptcha/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.onload = () => setRecaptchaReady(true);
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  useEffect(() => {
    if (!recaptchaReady || !recaptchaRef.current || !window.grecaptcha || typeof window.grecaptcha.render !== "function") return undefined;
    widgetIdRef.current = window.grecaptcha.render(recaptchaRef.current, {
      sitekey: RECAPTCHA_SITE_KEY,
      callback: token => setRecaptchaToken(token),
      "expired-callback": () => setRecaptchaToken(""),
      "error-callback": () => setRecaptchaToken("")
    });
    return () => {
      if (window.grecaptcha && widgetIdRef.current !== null) {
        window.grecaptcha.reset(widgetIdRef.current);
      }
    };
  }, [recaptchaReady]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!recaptchaToken) {
      alert("Please complete the reCAPTCHA.");
      return;
    }
    setSending(true);
    setSendStatus("");

    const body = `
      <h2>Contact form submission</h2>
      <p><strong>Name:</strong> ${name}</p>
      <p><strong>Company:</strong> ${company}</p>
      <p><strong>Phone:</strong> ${phone}</p>
      <p><strong>Email:</strong> ${email}</p>
      <p><strong>Subject:</strong> ${subject}</p>
      <p><strong>Message:</strong><br/>${message.replace(/\n/g, "<br/>")}</p>
      <p><strong>reCAPTCHA token:</strong> ${recaptchaToken}</p>
    `;

    try {
      await sendEmailJS({
        from_name: name,
        from_email: email,
        company,
        phone,
        subject: subject || "Contact Request",
        message: message,
        message_html: body,
      });
      setSendStatus("Message sent successfully.");
      setName("");
      setCompany("");
      setPhone("");
      setEmail("");
      setSubject("");
      setMessage("");
      setRecaptchaToken("");
      if (window.grecaptcha && widgetIdRef.current !== null) {
        window.grecaptcha.reset(widgetIdRef.current);
      }
    } catch (error) {
      console.error(error);
      setSendStatus("Failed to send message. Please try again later.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="contact-page">

      {/* HERO */}
      <div
        className="contact-hero"
        style={{
          backgroundImage:
            "url('https://steelstrong.com/blogs/wp-content/uploads/2023/12/ss.jpg')",
        }}
      >
        <div className="contact-overlay">
          <div className="container text-center hero-content">
            <h1>Contact Us</h1>
            <p>
              HP Valves & Fittings is ready to provide the right solution for
              your industrial needs.
            </p>
          </div>
        </div>
      </div>

      {/* CONTACT CARD */}
      <div className="container contact-card-wrapper">
        <div className="row contact-card align-items-stretch">

          {/* LEFT INFO */}
          <div className="col-lg-6 contact-info">

            <div className="contact-item">
              <div className="contact-icon"><i className="fas fa-map-marker-alt"></i></div>
              <div>
                <h6>Head Office</h6>
                <p>
                  HP Flow Solutions Australia<br />
                  Unit 3, 5 Murphy Street O'Connor<br />
                  Perth Western Australia 6163, Australia
                </p>
              </div>
            </div>

            <div className="contact-item">
              <div className="contact-icon"><i className="fas fa-envelope"></i></div>
              <div>
                <h6>Email</h6>
                <p>sales@hpfs.com.au</p>
              </div>
            </div>

            <div className="contact-item">
              <div className="contact-icon"><i className="fas fa-phone-alt"></i></div>
              <div>
                <h6>Contact No.</h6>
                <p>+61 439602113</p>
              </div>
            </div>

          </div>

          {/* FORM */}
          <div className="col-lg-6 contact-form">
            <div className="formHeader">
              <h3>Get in Touch</h3>
              <span>Have a question or need a quote? Send us a message!</span>
                
              
            </div>
            <form onSubmit={handleSubmit}>
              <div className="row g-3">

                <div className="col-md-6">
                  <input
                    className="form-control"
                    placeholder="Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </div>

                <div className="col-md-6">
                  <input
                    className="form-control"
                    placeholder="Company"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                  />
                </div>

                <div className="col-md-6">
                  <input
                    className="form-control"
                    placeholder="Phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                </div>

                <div className="col-md-6">
                  <input
                    className="form-control"
                    type="email"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>

                <div className="col-12">
                  <input
                    className="form-control"
                    placeholder="Subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                  />
                </div>

                <div className="col-12">
                  <textarea
                    className="form-control"
                    rows="4"
                    placeholder="Message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    required
                  ></textarea>
                </div>

                <div className="col-12">
                  <div ref={recaptchaRef} style={{ marginBottom: "12px" }} />
                  <button className="contact-btn" type="submit" disabled={!recaptchaToken || sending}>
                    {sending ? "Sending..." : "Send Message"}
                  </button>
                  {sendStatus && <div className="send-status">{sendStatus}</div>}
                </div>

              </div>
            </form>

          </div>

        </div>
      </div>

    

    </section>
  );
}